import { create } from 'zustand';

import { queryClient } from '@/api/queryClient.ts';
import {
  addEPLogo,
  addEPMembers,
  addFileToEP,
  createEP,
  createEPChat,
  deleteEP,
  deleteFileFromEP,
  getAllEPs,
  getCurrentEP,
  removeEPMember,
  updateEP,
  updateEPStatus,
} from '@/store/events/events-projects/events-projects.api.ts';
import type {
  AddedUser,
  CreateProjectRequest,
  EventsProjectResponse,
  UpdateProjectRequest,
} from '@/store/events/events-projects/events-projects.dto.ts';
import { toast } from '@/utils/toast.ts';

type EventProjectsTypes = {
  eventProjectsCount: number;
  eventProjects: EventsProjectResponse[];
  eventProject: EventsProjectResponse | null;
  isLoading: boolean;
  isSaving: boolean;
  getAllEventProjects: () => Promise<void>;
  getEventProject: (id: string) => Promise<void>;
  addEventProject: (
    payload: CreateProjectRequest,
    coverImage?: File | null,
  ) => Promise<EventsProjectResponse | null>;
  updateEventProject: (id: string, payload: UpdateProjectRequest) => Promise<void>;
  removeEventProject: (id: string) => Promise<void>;
  addFileToEventProject: (id: string, file: any) => Promise<void>;
  removeFileFromEventProject: (id: string, fileId: any) => Promise<void>;
  updateEventProjectsStatus: (id: string, status: string) => Promise<void>;
  createEventProjectChat: (
    name: string,
    description: string,
    projectId: string,
  ) => Promise<string | null>;
  addEventProjectLogo: (id: string, file: File) => Promise<void>;
  addEventProjectsMembers: (id: string, members: AddedUser[]) => Promise<void>;
  removeEventProjectsMember: (id: string, personId: string) => Promise<void>;
};

export const useEventProjectsStore = create<EventProjectsTypes>((set, get) => ({
  eventProjects: [],
  eventProjectsCount: 0,
  eventProject: null,
  isLoading: false,
  isSaving: false,
  getAllEventProjects: async () => {
    set({ isLoading: true });
    try {
      const { data } = await queryClient.fetchQuery({
        queryKey: ['event-projects'],
        queryFn: getAllEPs,
      });
      set({ eventProjects: data.items, eventProjectsCount: data.count });
    } catch {
      toast.error('Couldn’t load the projects. Please try again.');
    } finally {
      set({ isLoading: false });
    }
  },
  getEventProject: async (id: string) => {
    set({ isLoading: true });
    try {
      const { data } = await queryClient.fetchQuery({
        queryKey: ['event-projects', id],
        queryFn: () => getCurrentEP(id),
      });

      set({ eventProject: data });
    } catch {
      toast.error('Couldn’t load the project. Please try again.');
    } finally {
      set({ isLoading: false });
    }
  },
  addEventProject: async (payload: CreateProjectRequest, coverImage?: File | null) => {
    set({ isSaving: true });
    try {
      const { data } = await createEP(payload);
      if (coverImage && data?.id) await get().addEventProjectLogo(data.id, coverImage);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getAllEventProjects();
      toast.success('Your project has been created.');
      return data ?? null;
    } catch {
      toast.error('Couldn’t create the project. Please try again.');
      return null;
    } finally {
      set({ isSaving: false });
    }
  },
  updateEventProject: async (id: string, payload: UpdateProjectRequest) => {
    set({ isSaving: true });
    try {
      await updateEP(id, payload);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getEventProject(id);
      toast.success('Your project has been updated.');
    } catch {
      toast.error('Couldn’t update the project. Please try again.');
    } finally {
      set({ isSaving: false });
    }
  },
  removeEventProject: async (id: string) => {
    set({ isSaving: true });
    try {
      await deleteEP(id);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getAllEventProjects();
      toast.success('Your project has been deleted.');
    } catch {
      toast.error('Couldn’t delete the project. Please try again.');
    } finally {
      set({ isSaving: false });
    }
  },
  addFileToEventProject: async (id: string, file: any) => {
    set({ isSaving: true });
    try {
      await addFileToEP(id, file);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getEventProject(id);
      toast.success('File added');
    } catch {
      toast.error('Couldn’t attach the file. Please try again.');
    } finally {
      set({ isSaving: false });
    }
  },
  removeFileFromEventProject: async (id: string, fileId: any) => {
    set({ isSaving: true });
    try {
      await deleteFileFromEP(id, fileId);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getEventProject(id);
      toast.success('File deleted');
    } catch {
      toast.error('Couldn’t attach the file. Please try again.');
    } finally {
      set({ isSaving: false });
    }
  },
  updateEventProjectsStatus: async (id: string, status: string) => {
    set({ isSaving: true });
    try {
      await updateEPStatus(id, status);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getEventProject(id);
      toast.success('Your project status has been updated.');
    } catch {
      toast.error('Couldn’t update the project status. Please try again.');
    } finally {
      set({ isSaving: false });
    }
  },
  createEventProjectChat: async (name: string, description: string, projectId: string) => {
    set({ isSaving: true });
    try {
      const { data } = await createEPChat(name, description, projectId);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getEventProject(projectId);
      return data?.id ?? null;
    } catch {
      toast.error('Couldn’t create the project chat. Please try again.');
      return null;
    } finally {
      set({ isSaving: false });
    }
  },
  addEventProjectLogo: async (id, file) => {
    try {
      await addEPLogo(id, file);
    } catch {
      toast.error('Couldn’t attach the file. Please try again.');
    }
  },
  addEventProjectsMembers: async (id: string, members: AddedUser[]) => {
    set({ isSaving: true });
    try {
      await addEPMembers(id, members);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getEventProject(id);
    } catch {
      toast.error('Couldn’t add the members. Please try again.');
    } finally {
      set({ isSaving: false });
    }
  },
  removeEventProjectsMember: async (id: string, personId: string) => {
    set({ isSaving: true });
    try {
      await removeEPMember(id, personId);
      await queryClient.invalidateQueries({ queryKey: ['event-projects'] });
      await get().getEventProject(id);
    } catch {
      toast.error('Couldn’t remove the member. Please try again.');
    } finally {
      set({ isSaving: false });
    }
  },
}));
